import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { kindIcon } from "@/pages/coach/methods/shared";
import { Bookmark, Copy, Pencil, Clock, Repeat } from "lucide-react";

export default function TemplatesTab({ refreshToken, onEdit, onChanged }) {
  const t = useT();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/methods", { params: { is_template: true } }); setRows(Array.isArray(data) ? data : []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load, refreshToken]);

  const applyTemplate = async (m) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/coach/methods/${m.id}/duplicate`);
      toast.success(t("cm.tpl_copied"));
      onChanged?.();
      if (data?.id) onEdit(data.id);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="cm-templates-tab">
      <p className="mb-4 text-sm text-muted-foreground">{t("cm.templates_sub")}</p>
      {rows === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Bookmark className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("cm.no_templates")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("cm.no_templates_long")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((m) => {
            const Icon = kindIcon(m.kind);
            return (
              <div key={m.id} data-testid={`cm-tpl-${m.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15"><Icon className="h-5 w-5 text-primary" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{m.title}</p>
                  {m.subtitle && <p className="truncate text-xs text-muted-foreground">{m.subtitle}</p>}
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1 font-semibold text-primary"><Bookmark className="h-3 w-3" /> {t(`cm.kind_${m.kind}`)}</span>
                    {m.duration_min ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {m.duration_min}m</span> : null}
                    <span className="inline-flex items-center gap-1"><Repeat className="h-3 w-3" /> {t("cm.tpl_done", { n: m.completions_count })}</span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button size="sm" className="gap-1 rounded-full" disabled={busy} onClick={() => applyTemplate(m)} data-testid={`cm-tpl-use-${m.id}`}><Copy className="h-3.5 w-3.5" /> {t("cm.tpl_use")}</Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => onEdit(m.id)} data-testid={`cm-tpl-edit-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
